import { Component, Input, Output, EventEmitter } from '@angular/core';
import {
  CreatedByOrgTypeEnum,
  CrossOpinionEnum,
  CrossOpinionOptionKeysEnum,
  CrossReference,
  CrossReferenceAssessmentUpdates,
  CrossReferencePermissions,
  CrossReferencesOpinionOption,
  User,
} from '../../../../types/types';

// Create new type by picking properties from CrossReference interface.
type CrossAssessment = Pick<
  CrossReference,
  | 'marketplaceDisputeCount'
  | 'marketplaceApproveCount'
  | 'opinion'
  | 'notes'
  | 'isGPOCross'
  | 'createdByOrgType'
  | 'createdByOrg'
  | 'crossReferenceType'
>;

@Component({
    selector: 'app-clinical-assessment',
    templateUrl: './clinical-assessment.component.html',
    styleUrls: ['./clinical-assessment.component.scss'],
    standalone: false
})
export class ClinicalAssessmentComponent {
  @Input('crossReference') crossAssessment?: CrossAssessment;
  @Input() user?: User;
  @Input() permissions?: CrossReferencePermissions;
  @Input() crossReferenceOpinionOptions?: CrossReferencesOpinionOption[];
  @Input() isShowingSearchResults = false;
  @Output() addNote = new EventEmitter();
  @Output() addCross = new EventEmitter();
  @Output() updateOpinion = new EventEmitter();

  // Buyer with correct permissions can update to any opinion for a seller created cross.
  get isSellerCross(): boolean {
    return (
      (this.crossAssessment?.createdByOrgType || '').toUpperCase() ===
      CreatedByOrgTypeEnum.SELLER
    );
  }

  // Buyer can ONLY update opinion for cross created by user's org or if it is a seller cross.
  // Buyer cross down vote equals removal.
  // Seller cross down vote by buyer equals down vote but not removal.
  get crossCreatedByUserOrg(): boolean {
    return (
      !!this.user?.organization.id &&
      this.user.organization.id === this.crossAssessment?.createdByOrg?.id
    );
  }

  onAddNote(noteMessage: string) {
    this.addNote.emit(noteMessage);
  }

  onUpdateOpinion({
    opinionOptionKey,
    isExplicitSelection, // Parameter may not be necessary at all.
  }: {
    opinionOptionKey: string;
    isExplicitSelection: boolean;
  }) {
    const opinionUpdates = this.getOpinionUpdates(
      opinionOptionKey,
      isExplicitSelection,
    );
    this.updateOpinion.emit({ opinionOptionKey, opinionUpdates });
  }

  onAddCross() {
    const opinionOptionKey = CrossOpinionOptionKeysEnum.CURRENT_PRODUCT;
    const opinionUpdates = this.getOpinionUpdates(opinionOptionKey);
    this.addCross.emit({ opinionOptionKey, opinionUpdates });
  }

  showAddCross() {
    return (
      !this.crossAssessment?.createdByOrg ||
      (this.user?.isSeller &&
        this.crossAssessment?.isGPOCross &&
        this.isShowingSearchResults &&
        this.crossAssessment?.createdByOrg?.id !== this.user?.organization.id)
    );
  }

  // TODO: move updates to service
  getOpinionUpdates(
    opinionOptionKey: string,
    isExplicitSelection = false,
  ): CrossReferenceAssessmentUpdates {
    const votedOpinionOption = this.crossReferenceOpinionOptions?.find(
      (option) => option.optionKey === opinionOptionKey,
    ) as CrossReferencesOpinionOption;

    // The note that will be posted with the opinion update.
    const feedbackDescription = votedOpinionOption!.feedbackDescription;

    let { marketplaceApproveCount = 0, marketplaceDisputeCount = 0 } =
      this.crossAssessment || {};

    const currentOpinion = this.crossAssessment?.opinion?.opinion || '';

    // Decrement from the opinion we are moving away from.
    if (currentOpinion === CrossOpinionEnum.APPROVE) {
      marketplaceApproveCount -= 1;
    } else if (currentOpinion === CrossOpinionEnum.DISPUTE) {
      marketplaceDisputeCount -= 1;
    }

    // Increment to the opinion we are moving to.
    if (votedOpinionOption.opinion === CrossOpinionEnum.APPROVE) {
      marketplaceApproveCount += 1;
    } else if (votedOpinionOption.opinion === CrossOpinionEnum.DISPUTE) {
      marketplaceDisputeCount += 1;
    }

    const crossReferenceAssessmentUpdates = {
      marketplaceApproveCount,
      marketplaceDisputeCount,
      opinion: { opinion: votedOpinionOption.opinion, feedbackDescription },
    };

    return crossReferenceAssessmentUpdates;
  }
}
